import React from 'react';
import { motion } from 'framer-motion';
import { Target, Award, Lightbulb, TrendingUp, Users, Rocket, CheckCircle, BookOpen } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const StrategicVision = () => {
  const stats = [
    {
      value: '85%',
      label: 'Placement Rate',
      description: 'Students placed or self-employed within 6 months',
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      value: '150+',
      label: 'Startup Mentees',
      description: 'Successfully launched startups',
      icon: Rocket,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      value: '100%',
      label: 'Industry Ready',
      description: 'Practical skills and real-world challenges',
      icon: Award,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  const features = [
    {
      icon: Lightbulb,
      title: 'Innovative Learning Model',
      description: 'Our Industry-Integrated Learning Model (IILM) blends academic rigor, hands-on projects, and entrepreneurship training, equipping students to be adaptable, skilled, and industry-ready professionals.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      gradient: 'from-orange-600 to-amber-600'
    },
    {
      icon: Users,
      title: 'Employability Focus',
      description: 'We focus on innovative learning model with emphasis on employability and entrepreneurship, ensuring every student is prepared for the competitive job market.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      gradient: 'from-indigo-600 to-blue-600'
    },
    {
      icon: CheckCircle,
      title: 'Proven Success',
      description: 'Our successful legacy in terms of mentoring to make students job-ready and launching startups demonstrates our commitment to excellence and measurable outcomes.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      gradient: 'from-emerald-600 to-green-600'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Strategic Vision Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full">
            <Target className="h-5 w-5 text-blue-600" />
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Strategic Vision</span>
          </div>
        </motion.div>

        {/* Impact Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-t-4 border-t-transparent hover:border-t-blue-600 group">
                  <CardContent className="p-6 text-center">
                    <div className={`${stat.bgColor} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent className={`h-8 w-8 ${stat.color}`} />
                    </div>
                    <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                    <div className="text-lg font-semibold text-gray-900 mb-2">{stat.label}</div>
                    <p className="text-sm text-gray-600">{stat.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Why Choose ISDE Academy - Title and Vision Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Choose <span className="text-blue-600">ISDE Academy?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            To establish itself as a training center of repute by bridging the gap between academic learning and industry requirements, mentoring students for real-world challenges with practical skills, problem-solving abilities, and an entrepreneurial mindset.
          </p>
        </motion.div>

        {/* Key Features */}
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-2xl transition-all duration-300 group overflow-hidden">
                  <div className={`h-2 bg-gradient-to-r ${feature.gradient}`}></div>
                  <CardContent className="p-6">
                    <div className={`${feature.bgColor} w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent className={`h-7 w-7 ${feature.color}`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Call to Action Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12"
        >
          <Card className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 border-none">
            <CardContent className="p-8 text-center text-white">
              <div className="flex items-center justify-center gap-2 mb-4">
                <BookOpen className="h-6 w-6" />
                <h3 className="text-2xl font-bold">Impact & Outcomes</h3>
              </div>
              <p className="text-lg opacity-95 max-w-4xl mx-auto leading-relaxed">
                With over <strong>85% of students placed or self-employed within six months</strong> and <strong>150+ mentees launching startups</strong>, our programs demonstrate measurable success in employability, innovation, and career readiness.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
